import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronDown, Mail, MapPin, Monitor, Smartphone } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Thumb, ThumbStack } from '../../../components/design-system/analytics';
// Straight from the files: the kit's index pulls in the page frame and, through it, the whole app.
import { RelativeTime } from '../../../components/design-system/admin/RelativeTime';
import { formatDateTime } from '../../../components/design-system/admin/format';
import { VIZ_LINKS } from '../../vendor/analytics/viz/links';
import { Session, STORY_STEPS, isLive, stepPhrase, subjectPhrase } from './activity-feed';

type T = (key: string, params?: Record<string, unknown>) => string;

/** One visit as a sentence: "A visitor from Lyon tried Rovere Naturale on the floor, then downloaded the result." */
export function sessionSentence(session: Session, t: T): string {
  const say = (p: { key: string; params?: Record<string, unknown> }) => t(`VIZ_ANALYTICS.ACTIVITY.STORY.${p.key}`, p.params);
  const unnamed = t('VIZ_ANALYTICS.ACTIVITY.STORY.A_PRODUCT');
  const shown = session.steps.slice(0, STORY_STEPS).map((s) => say(stepPhrase(s, unnamed)));
  const rest = session.steps.length - shown.length;
  const body = shown.join(t('VIZ_ANALYTICS.ACTIVITY.STORY.THEN'));
  return t('VIZ_ANALYTICS.ACTIVITY.STORY.SENTENCE', {
    subject: say(subjectPhrase(session)),
    steps: rest > 0 ? `${body}${t('VIZ_ANALYTICS.ACTIVITY.STORY.AND_MORE', { count: rest })}` : body,
  });
}

const deviceIcon = (device?: string) => (device && /mobile|phone|tablet/i.test(device) ? Smartphone : Monitor);

/**
 * The feed of visits. `compact` for a dashboard card: one line each, no
 * pictures, no detail toggle.
 */
export function ActivityFeed({
  sessions,
  loading,
  compact,
  limit,
  now = new Date(),
  emptyText,
  className,
}: {
  sessions: Session[];
  loading?: boolean;
  compact?: boolean;
  limit?: number;
  now?: Date;
  emptyText?: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<Set<string>>(new Set());
  const shown = limit ? sessions.slice(0, limit) : sessions;

  if (loading) {
    return (
      <div className={cn('flex flex-col gap-3', className)}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex gap-3">
            <Skeleton className="size-8 rounded-full" />
            <div className="flex flex-1 flex-col gap-1.5">
              <Skeleton className="h-4 w-4/5" />
              <Skeleton className="h-3 w-1/3" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!shown.length) {
    return <p className="py-8 text-center text-sm text-muted-foreground">{emptyText ?? t('VIZ_ANALYTICS.ACTIVITY.EMPTY')}</p>;
  }

  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <ul className={cn('flex flex-col divide-y', className)} data-testid="activity-feed">
      {shown.map((session) => {
        const live = isLive(session, now);
        const Device = deviceIcon(session.device);
        const expanded = open.has(session.id);
        const place = [session.city, session.country].filter(Boolean).join(', ');
        return (
          <li key={session.id} className={cn('flex gap-3', compact ? 'py-2.5' : 'py-3.5')} data-intent={session.intent || undefined}>
            <span
              className={cn(
                'flex shrink-0 items-center justify-center rounded-full border bg-muted text-xs font-medium text-muted-foreground',
                compact ? 'size-7' : 'size-8',
              )}
              aria-hidden
            >
              {session.email ? session.email.charAt(0).toUpperCase() : <MapPin className="size-3.5" />}
            </span>
            <div className="min-w-0 flex-1">
              <p className={cn('text-sm leading-snug', compact && 'line-clamp-2')}>{sessionSentence(session, t as T)}</p>
              <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                {live ? (
                  <span className="inline-flex items-center gap-1 text-success">
                    <span className="size-1.5 rounded-full bg-success" aria-hidden />
                    {t('VIZ_ANALYTICS.ACTIVITY.LIVE')}
                  </span>
                ) : (
                  <RelativeTime value={session.end} />
                )}
                {!compact && place && <span className="truncate">{place}</span>}
                {!compact && session.device && (
                  <span className="inline-flex items-center gap-1">
                    <Device className="size-3" aria-hidden />
                    {t(`VIZ_ANALYTICS.DEVICE.${/mobile|phone/i.test(session.device) ? 'MOBILE' : /tablet/i.test(session.device) ? 'TABLET' : 'DESKTOP'}`)}
                  </span>
                )}
              </p>
              {!compact && session.products.length > 0 && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <ThumbStack items={session.products.map((p) => ({ key: p.id, src: p.thumbnailPath, alt: p.name }))} max={6} size="sm" />
                  {session.steps.length > 1 && (
                    <Button variant="ghost" size="xs" onClick={() => toggle(session.id)} aria-expanded={expanded}>
                      {t(expanded ? 'VIZ_ANALYTICS.ACTIVITY.HIDE_STEPS' : 'VIZ_ANALYTICS.ACTIVITY.SHOW_STEPS', { count: session.steps.length })}
                      <ChevronDown className={cn('transition-transform', expanded && 'rotate-180')} />
                    </Button>
                  )}
                </div>
              )}
              {!compact && expanded && (
                <ol className="mt-2 flex flex-col gap-1.5 border-s ps-3">
                  {session.steps.map((step, i) => (
                    <li key={i} className="flex items-center gap-2 text-xs">
                      <span className="w-12 shrink-0 tabular-nums text-muted-foreground" title={formatDateTime(step.at)}>
                        {new Date(step.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {step.products[0] && <Thumb src={step.products[0].thumbnailPath} alt={step.products[0].name} size="xs" />}
                      <span className="min-w-0 flex-1 truncate">
                        {t(`VIZ_ANALYTICS.ACTIVITY.STORY.${stepPhrase(step, t('VIZ_ANALYTICS.ACTIVITY.STORY.A_PRODUCT')).key}`, stepPhrase(step, t('VIZ_ANALYTICS.ACTIVITY.STORY.A_PRODUCT')).params)}
                        {step.count > 1 && <span className="text-muted-foreground"> ×{step.count}</span>}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
            {session.email && !compact && (
              <Button asChild variant="outline" size="sm" className="shrink-0 self-start">
                <Link to={VIZ_LINKS.compose(session.email)} aria-label={t('VIZ_ANALYTICS.ACTIVITY.EMAIL_THEM', { email: session.email })}>
                  <Mail />
                  <span className="hidden sm:inline">{t('VIZ_ANALYTICS.ACTIVITY.EMAIL')}</span>
                </Link>
              </Button>
            )}
          </li>
        );
      })}
    </ul>
  );
}
